import { useStore } from '@tanstack/react-form';
import { Card, Checkbox, Icon, Input, LearnMoreLink, Text } from '@ui/themed';
import { t } from 'i18next';
import { SETTING_KEY_TO_LABEL } from '../../constants';
import { useCustomerGroupBuilderForm } from '../../hooks/use-customer-group-builder-form';
import {
  SettingStockDisplaySchema,
  type SettingStockDisplayState,
} from '../../types/customer-group.schema';
import { ResetSettingsButton } from './reset-settings-button';

const NUMERIC_KEYS = ['max', 'low', 'last'] as const;

export function StockDisplayCard() {
  const form = useCustomerGroupBuilderForm();
  const maxValue = useStore(form.store, (state) => state.values.settings?.stockDisplay?.max);

  return (
    <Card showBorder>
      <Card.Header collapsible headingLevel="h2" icon="checkCircle">
        {SETTING_KEY_TO_LABEL.stockDisplay}
      </Card.Header>
      <Card.Body>
        <Text variant="secondary">
          {t('features:customer_group.components.stock_display_card.description')}{' '}
          <LearnMoreLink link="https://docs.sparklayer.io/customers#RUp35" />
        </Text>
        {Object.keys(SettingStockDisplaySchema.shape).map((key) => {
          const stockDisplayKey = key as keyof SettingStockDisplayState;
          if (!key.startsWith('show_') && !key.startsWith('hide_')) return null;
          return (
            <form.Field key={stockDisplayKey} name={`settings.stockDisplay.${stockDisplayKey}`}>
              {(field) => (
                <Checkbox
                  label={t(
                    `features:customer_group.components.stock_display_card.${stockDisplayKey}.label`,
                  )}
                  checked={!!field.state.value}
                  onChange={() => field.handleChange((prev) => !prev)}
                  description={t(
                    `features:customer_group.components.stock_display_card.${stockDisplayKey}.description`,
                  )}
                />
              )}
            </form.Field>
          );
        })}
        <div className="grid grid-cols-3 gap-3">
          {NUMERIC_KEYS.map((key) => (
            <form.Field key={key} name={`settings.stockDisplay.${key}`}>
              {(field) => (
                <Input
                  label={
                    (
                      <>
                        <span className={key === 'max' ? 'text-green-500' : 'text-yellow-500'}>
                          <Icon name="circle" size="xs" />
                        </span>
                        <p>
                          {t(`features:customer_group.components.stock_display_card.${key}.label`)}
                        </p>
                      </>
                    ) as unknown as string
                  }
                  value={field.state.value?.toString() ?? '0'}
                  error={!field.state.meta.isValid ? field.state.meta.errors[0].message : undefined}
                  onChange={field.handleChange}
                />
              )}
            </form.Field>
          ))}
        </div>
        <div className="flex flex-col gap-1">
          {NUMERIC_KEYS.map((key) => (
            <Text key={key} variant="secondary">
              {`${t(`features:customer_group.components.stock_display_card.${key}.label`)}: `}
              {t(`features:customer_group.components.stock_display_card.${key}.description`, {
                number: key === 'max' ? (maxValue ?? '0') : undefined,
              })}
            </Text>
          ))}
        </div>
        <div className="flex justify-start">
          <form.Field name="settings.stockDisplay">
            {(field) => <ResetSettingsButton field={field} />}
          </form.Field>
        </div>
      </Card.Body>
    </Card>
  );
}
